import React from 'react'
import { Link } from 'react-router-dom'
import { getAgentCardMeta } from '../../lib/agentCardMeta'
import { AGENT_BADGE_STYLES } from '../../lib/useAgentPipelineReadiness'
import './agentsPage.css'

const AGENT_TASK_CREATOR = 'basecamp_task_creator'
const KNOWLEDGE_ASSISTANT_KEY = 'knowledge_assistant'

/**
 * @param {Object} props
 * @param {Record<string, unknown>} props.row
 * @param {string} [props.categoryKey]
 * @param {boolean} [props.showStepBadge]
 * @param {string[]} [props.cornerBadges]
 * @param {string} [props.statusHint]
 * @param {boolean} props.canRun
 * @param {boolean} [props.runDisabled]
 * @param {string} [props.runDisabledTitle]
 * @param {() => void} props.onRun
 */
export default function AgentRunCard({
  row,
  categoryKey,
  showStepBadge = false,
  cornerBadges = [],
  statusHint = '',
  canRun,
  runDisabled = false,
  runDisabledTitle = '',
  onRun,
}) {
  const { icon: AgentIcon, accent } = getAgentCardMeta(row.agent_key, categoryKey)
  const iconClass = `agents-admin-card-icon agents-admin-card-icon--${accent}`

  const badges = [...cornerBadges]
  if (row.is_active === false && !badges.includes('inactive')) badges.push('inactive')

  const attentionBadges = new Set(['new_data', 'ready', 'failed', 'sync_pending'])
  const statusNeedsAttention = cornerBadges.some((b) => attentionBadges.has(b))
  const isKnowledge = row.agent_key === KNOWLEDGE_ASSISTANT_KEY

  const cardClass = [
    'agents-admin-card',
    row.is_active === false ? 'agents-admin-card--inactive' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <article className={cardClass}>
      <div className="agents-admin-card-header">
        <div className={iconClass}>
          <AgentIcon size={22} strokeWidth={2} />
        </div>
        <div className="agents-admin-card-heading">
          <h3 className="agents-admin-card-title">{row.display_name || row.agent_key}</h3>
          <div className="agents-admin-card-badges">
            {showStepBadge && row.step_order != null ? (
              <span className="agents-admin-card-badge agents-admin-card-badge--step">
                Step {row.step_order}
              </span>
            ) : null}
            {badges.map((key) => {
              const style = AGENT_BADGE_STYLES[key]
              if (!style) return null
              return (
                <span
                  key={key}
                  className="agents-admin-card-badge"
                  style={{ background: style.bg, color: style.color }}
                >
                  {style.label}
                </span>
              )
            })}
          </div>
        </div>
      </div>

      <div className="agents-admin-card-body">
        <p className="agents-admin-card-desc">{row.description || 'No description.'}</p>

        {statusHint ? (
          <p
            className={
              statusNeedsAttention
                ? 'agents-admin-card-meta agents-admin-card-meta--warn'
                : 'agents-admin-card-meta'
            }
          >
            {statusHint}
          </p>
        ) : (
          <p className="agents-admin-card-meta">No status signal right now.</p>
        )}

        {isKnowledge ? (
          <p className="agents-admin-card-meta">
            Chat lives on{' '}
            <Link to="/knowledge" style={{ color: '#2563eb', fontWeight: 600 }}>
              /knowledge
            </Link>
            .
          </p>
        ) : null}

        {row.is_active === false ? (
          <p className="agents-admin-card-meta agents-admin-card-meta--warn">
            Disabled — will not run when inactive.
          </p>
        ) : null}

        {row.agent_key === AGENT_TASK_CREATOR && runDisabled && canRun ? (
          <p className="agents-admin-card-meta">Complete Step 1 or select a saved analyzer run in the dialog.</p>
        ) : null}
      </div>

      {canRun ? (
        <div className="agents-admin-card-actions">
          {isKnowledge ? (
            <Link to="/knowledge" className="agents-admin-card-btn agents-admin-card-btn--primary">
              Open chat
            </Link>
          ) : (
            <button
              type="button"
              disabled={runDisabled}
              title={runDisabled ? runDisabledTitle : 'Run this pipeline stage with real project data'}
              onClick={onRun}
              className="agents-admin-card-btn agents-admin-card-btn--primary"
            >
              Run stage
            </button>
          )}
        </div>
      ) : (
        <p className="agents-admin-card-readonly">Super admin required to run agent stages.</p>
      )}
    </article>
  )
}
